import {
  BrandButton,
  ConnectWalletButton,
} from "../../../components/brand/BrandButton";
import { useSession } from "@/features/session";
import {
  Copy,
  ExternalLink,
  RefreshCw,
  LogOut,
  Wallet,
  Shield,
  Check,
  ArrowUpRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useTranslation } from "@/i18n";
import { useBrowserWalletStore, TransferDialog } from "@/features/wallet";
import { useSolanaBalance } from "@/hooks/useSolanaBalance";
import { useSolanaTokenAccounts } from "@/hooks/useSolanaTokenAccounts";
import { getExplorerAddressUrl } from "@/lib/solana/format";
import { COMMON_TOKEN_MINTS } from "@/config/constants";
import { useState, useMemo } from "react";

type TransferTokenId = "SOL" | "USDC";

interface TokenRowProps {
  symbol: string;
  tokenBalance: number;
  decimals: number;
  dotColor: string;
  tokenId: TransferTokenId;
  /** Opens the transfer dialog for this token. */
  onSend: (token: TransferTokenId) => void;
  /** Accessible verb for the send control (localised by the caller). */
  sendLabel: string;
}

// Token row for balance display — module scope, so it is one component type
// across renders (a component created inside render remounts on every render).
function TokenRow({
  symbol,
  tokenBalance,
  decimals,
  dotColor,
  tokenId,
  onSend,
  sendLabel,
}: TokenRowProps) {
  return (
    <div className="group flex items-center justify-between py-1.5 px-2 -mx-2 rounded-lg hover:bg-secondary/50 transition-colors">
      <div className="flex items-center gap-2">
        <div className={`h-2 w-2 rounded-full ${dotColor}`} />
        <span className="text-sm text-muted-foreground">{symbol}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono tabular-nums text-foreground">
          {tokenBalance.toFixed(decimals <= 6 ? Math.min(decimals, 4) : 4)}
        </span>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSend(tokenId);
          }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all"
          aria-label={`${sendLabel} ${symbol}`}
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export interface LoginButtonProps {
  className?: string;
  size?: "sm" | "default" | "lg" | "xl";
  showFullAddress?: boolean;
  withDropdown?: boolean;
}

/**
 * LoginButton Component
 *
 * Handles authentication flow:
 * 1. Not connected → Click opens browser wallet modal
 * 2. Connected but not authenticated → Click opens signature dialog
 * 3. Authenticated → Shows wallet address with logout dropdown
 */
export function LoginButton({
  className,
  size = "default",
  showFullAddress = false,
  withDropdown = true,
}: LoginButtonProps) {
  const { t } = useTranslation();
  const {
    isActive: isAuthenticated,
    walletAddress: wallet,
    logout,
  } = useSession();
  const openModal = useBrowserWalletStore((s) => s.openModal);

  // Wallet store state (for display in authenticated dropdown)
  const walletType = useBrowserWalletStore((state) => state.walletType);
  const walletName = useBrowserWalletStore((state) => state.walletName);
  const network = useBrowserWalletStore((state) => state.network);

  // Balance hooks
  const {
    balance,
    isFetching: balanceLoading,
    refetch: refetchBalance,
  } = useSolanaBalance();
  const {
    tokenAccounts,
    isFetching: tokensFetching,
    refetch: refetchTokens,
  } = useSolanaTokenAccounts();

  // Derive USDC balance from tokenAccounts
  const { paymentBalance, paymentDecimals } = useMemo(() => {
    if (tokenAccounts.length === 0) {
      return { paymentBalance: 0, paymentDecimals: 6 };
    }
    const paymentAccount = tokenAccounts.find(
      (ta) => ta.mint === COMMON_TOKEN_MINTS.USDC,
    );
    return {
      paymentBalance: paymentAccount?.uiAmount ?? 0,
      paymentDecimals: 6,
    };
  }, [tokenAccounts]);

  const isRefreshing = balanceLoading || tokensFetching;

  // Copy address feedback state
  const [copied, setCopied] = useState(false);

  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferToken, setTransferToken] = useState<TransferTokenId>("SOL");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const formatAddress = (address: string): string => {
    if (showFullAddress) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Wallet icon based on type
  const WalletIcon = walletType === "ledger" ? Shield : Wallet;

  // Get display name for wallet type
  const getWalletDisplayName = (): string => {
    if (walletName) return walletName;
    if (walletType === "ledger") return "Ledger";
    return "Hot Wallet";
  };

  // Handle copy address
  const handleCopyAddress = () => {
    if (!wallet) return;
    navigator.clipboard.writeText(wallet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle view on explorer
  const handleViewExplorer = () => {
    if (!wallet) return;
    const url = getExplorerAddressUrl(wallet, network);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Handle opening transfer dialog from a token row
  const handleOpenTransfer = (token: TransferTokenId) => {
    setTransferToken(token);
    setDropdownOpen(false);
    // Small delay to let dropdown close animation finish
    setTimeout(() => setTransferOpen(true), 150);
  };

  const handleRefreshBalances = () => {
    refetchBalance();
    refetchTokens();
  };

  // State 1: Authenticated - Show wallet address with dropdown (highest priority)
  if (isAuthenticated && wallet) {
    if (withDropdown) {
      return (
        <>
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <BrandButton
                variant="outline"
                size={size}
                className={`border-outline-variant/40 hover:border-primary/30 bg-surface-container-lowest hover:bg-primary/[0.04] text-foreground font-medium transition-all ${className || ""}`}
                aria-label={t("auth.loggedInAs", { address: wallet })}
              >
                <div className="relative mr-2">
                  <WalletIcon className="w-4 h-4 text-primary" aria-hidden="true" />
                  <div className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-brand-blue-400" />
                </div>
                <span className="font-mono text-sm">
                  {formatAddress(wallet)}
                </span>
              </BrandButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-80 bg-popover border-outline-variant/30 shadow-lg rounded-2xl p-0 overflow-hidden"
            >
              {/* Header: Wallet identity */}
              <div className="px-4 py-3.5 border-b border-outline-variant/15">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-full bg-primary/10">
                    <WalletIcon
                      className="h-4 w-4 text-primary"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {getWalletDisplayName()}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-brand-blue-400" />
                      <p className="text-xs text-muted-foreground font-mono truncate">
                        {formatAddress(wallet)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Balance section */}
              <div className="mx-3 my-2 p-3 bg-surface-container-low/60 rounded-xl">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                    {t("wallet.balance")}
                  </span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRefreshBalances();
                    }}
                    disabled={isRefreshing}
                    className="p-1 rounded-md hover:bg-surface-container transition-colors disabled:opacity-50"
                    aria-label={t("wallet.refresh")}
                  >
                    <RefreshCw
                      className={`h-3 w-3 text-muted-foreground ${isRefreshing ? "animate-spin" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div className="space-y-0.5">
                  <TokenRow
                    symbol="SOL"
                    tokenBalance={balance ?? 0}
                    decimals={9}
                    dotColor="bg-brand-blue-400"
                    tokenId="SOL"
                    onSend={handleOpenTransfer}
                    sendLabel={t("wallet.send")}
                  />
                  <TokenRow
                    symbol={t("wallet.paymentToken")}
                    tokenBalance={paymentBalance}
                    decimals={paymentDecimals}
                    dotColor="bg-blue-500"
                    tokenId="USDC"
                    onSend={handleOpenTransfer}
                    sendLabel={t("wallet.send")}
                  />
                </div>
              </div>

              {/* Action buttons row */}
              <div className="px-3 pb-2 grid grid-cols-2 gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCopyAddress();
                  }}
                  className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-surface-container-low hover:bg-surface-container text-muted-foreground hover:text-foreground text-xs font-medium transition-colors"
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-primary" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  {copied ? t("common.copied") : t("wallet.copyAddress")}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleViewExplorer();
                  }}
                  className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-surface-container-low hover:bg-surface-container text-muted-foreground hover:text-foreground text-xs font-medium transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t("wallet.viewOnExplorer")}
                </button>
              </div>

              {/* Transfer button */}
              <div className="px-3 pb-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenTransfer("SOL");
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary/[0.08] hover:bg-primary/[0.12] border border-primary/15 hover:border-primary/25 text-primary text-sm font-medium transition-all"
                >
                  <ArrowUpRight className="h-4 w-4" />
                  {t("wallet.transfer")}
                </button>
              </div>

              {/* Disconnect */}
              <div className="px-3 pb-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logout();
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/[0.05] text-xs font-medium transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {t("wallet.disconnect")}
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* TransferDialog rendered outside dropdown */}
          <TransferDialog
            open={transferOpen}
            onOpenChange={setTransferOpen}
            defaultToken={transferToken}
            walletAddress={wallet}
            solBalance={balance}
            paymentBalance={paymentBalance}
            paymentDecimals={paymentDecimals}
            paymentMint={COMMON_TOKEN_MINTS.USDC}
            governanceBalance={0}
            governanceDecimals={9}
            onTransferComplete={handleRefreshBalances}
          />
        </>
      );
    }

    return (
      <div className={`flex items-center gap-3 ${className || ""}`}>
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-border"
          role="status"
          aria-label={t("auth.loggedInAs", { address: wallet })}
        >
          <Wallet className="w-4 h-4 text-primary" aria-hidden="true" />
          <span className="text-sm font-mono text-primary">
            {formatAddress(wallet)}
          </span>
        </div>
        <BrandButton
          onClick={logout}
          variant="ghost"
          size={size}
          aria-label={t("auth.logoutFromWallet")}
          className="text-muted-foreground hover:text-red-600 hover:bg-secondary"
        >
          <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
          {t("auth.logout")}
        </BrandButton>
      </div>
    );
  }

  // State 2: Not authenticated - Show "Connect Wallet" button
  return (
    <ConnectWalletButton
      onClick={() => openModal()}
      size={size}
      className={className}
      aria-label={t("auth.connectWalletToSignIn")}
    >
      {t("auth.connectWallet")}
    </ConnectWalletButton>
  );
}

export function LoginButtonCompact(props: Omit<LoginButtonProps, "size">) {
  return <LoginButton {...props} size="sm" />;
}

export function LoginButtonLarge(props: Omit<LoginButtonProps, "size">) {
  return <LoginButton {...props} size="lg" />;
}
